import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmailSource, MyEmails, UserEmail } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Sheet } from "./ui";
import { CodeBoxes } from "./CodeBoxes";
import { account } from "@pagemates/shared/copy";

const txt = account.emails;

/*
 * Email addresses (PRD "Email addresses and email sign-in"; mock artifact 6Pet7JpXvLjCqv3zkhFP29): the
 * "Send emails to" sheet in notification settings, and the address list under Account. Only confirmed
 * addresses get email or sign in; a new one gets a code and takes over only once it's confirmed.
 */

export const SOURCE: Record<EmailSource, string> = { google: txt.fromGoogle, discord: txt.fromDiscord, apple: txt.fromApple, email: txt.fromASignIn, manual: txt.addedByYou };

export function useMyEmails(enabled = true) {
  return useQuery({ queryKey: ["my-emails"], queryFn: api.myEmails, enabled });
}

/** After any change: the list, and the notification settings that show the address. */
function useEmailRefresh() {
  const qc = useQueryClient();
  return (data?: MyEmails) => {
    if (data) qc.setQueryData(["my-emails"], data);
    void qc.invalidateQueries({ queryKey: ["my-emails"] });
    void qc.invalidateQueries({ queryKey: ["notif-prefs"] });
    void qc.invalidateQueries({ queryKey: ["me"] });
  };
}

/** "Enter the code we sent to …", with the six boxes and a way to send it again. */
function PendingCode({ pending, onConfirmed, onResend, resending }: { pending: NonNullable<MyEmails["pending"]>; onConfirmed: () => void; onResend: () => void; resending: boolean }) {
  const refresh = useEmailRefresh();
  const [code, setCode] = useState("");
  const confirm = useMutation({
    mutationFn: (c: string) => api.confirmEmail(pending.address, c),
    onSuccess: (d) => { refresh(d); onConfirmed(); },
    onError: () => setCode(""),
  });
  return (
    <div className="pending-code">
      <p className="small" style={{ margin: 0 }}>{txt.enterTheCodeWe}<b>{pending.address}</b>.</p>
      <CodeBoxes value={code} onChange={setCode} onComplete={(c) => confirm.mutate(c)} disabled={confirm.isPending} autoFocus />
      {confirm.isError && <p className="error small">{describeError(confirm.error)}</p>}
      <button type="button" className="linkish small" disabled={resending} onClick={onResend}>{txt.sendItAgain}</button>
    </div>
  );
}

/** Settings → Notifications → "Change": choose a confirmed address, confirm one, or add one. */
export function AddressSheet({ onClose }: { onClose: () => void }) {
  const q = useMyEmails();
  const refresh = useEmailRefresh();
  const [address, setAddress] = useState("");
  const choose = useMutation({ mutationFn: api.setNotifyEmail, onSuccess: (d) => refresh(d) });
  const add = useMutation({
    mutationFn: (a: { address: string }) => api.addEmail(a.address, true),
    onSuccess: (d) => { refresh(d); if (d.confirmed) onClose(); },
  });
  const list = q.data?.addresses ?? [];
  const usable = list.filter((e) => e.confirmed && !e.undeliverable);
  const pending = q.data?.pending ?? null;
  // The one waiting for its code shows below with its boxes, not in this list.
  const unconfirmed = list.filter((e) => !e.confirmed && e.address !== pending?.address);
  const current = usable.find((e) => e.forNotifications);
  return (
    <Sheet label={txt.sendEmailsTo} onClose={onClose} className="addr-sheet">
      {usable.length > 0 && (
        <div className="addr-list" role="radiogroup" aria-label={txt.sendEmailsTo}>
          {usable.map((e) => (
            <label key={e.id} className="addr-opt">
              <input type="radio" name="notify-email" checked={e.forNotifications} disabled={choose.isPending} onChange={() => choose.mutate(e.id)} />
              <span className="addr-main"><span className="addr">{e.address}</span>
                <span className="small muted addr-sub">{SOURCE[e.source]}{e.isRelay && <span className="chip-s">{txt.hiddenByApple}</span>}</span></span>
            </label>
          ))}
          {unconfirmed.map((e) => (
            <div key={e.id} className="addr-opt off">
              <span className="addr-main"><span className="addr muted">{e.address}</span>
                <span className="small muted">{SOURCE[e.source]} · {e.undeliverable ? txt.weCouldnTDeliver : txt.notConfirmed} · <button type="button" className="linkish small" disabled={add.isPending} onClick={() => add.mutate({ address: e.address })}>{txt.confirmIt}</button></span></span>
            </div>
          ))}
        </div>
      )}
      {usable.length > 0 && <p className="small muted" style={{ margin: 0 }}>{txt.choosingAConfirmedAddress}</p>}
      {current?.isRelay && <p className="small muted" style={{ margin: 0 }}>{txt.appleForwardsTheseTo}</p>}
      {choose.isError && <p className="error small">{describeError(choose.error)}</p>}
      {pending ? (
        <PendingCode pending={pending} onConfirmed={onClose} resending={add.isPending} onResend={() => add.mutate({ address: pending.address })} />
      ) : (
        <form className="addr-new" onSubmit={(e) => { e.preventDefault(); if (address.trim()) add.mutate({ address }); }}>
          <label htmlFor="new-notify-email" className="small" style={{ fontWeight: 600 }}>{usable.length ? txt.useADifferentAddress : txt.addAnAddress}</label>
          <input id="new-notify-email" className="input" type="email" autoComplete="email" placeholder="you@example.com" value={address} onChange={(e) => setAddress(e.target.value)} />
          <button type="submit" className="btn primary block" disabled={!address.trim() || add.isPending}>{add.isPending ? txt.sending : txt.sendItACode}</button>
          <p className="small muted" style={{ margin: 0 }}>{txt.nothingElseGoesThere(current ? txt.untilThenEmailsKeep(current.address) : "")}</p>
        </form>
      )}
      {add.isError && <p className="error small">{describeError(add.error)}</p>}
    </Sheet>
  );
}

/** Settings → Account: every address, what it's for, and removing or confirming one. */
export function EmailAddresses() {
  const q = useMyEmails();
  const refresh = useEmailRefresh();
  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const add = useMutation({ mutationFn: (a: string) => api.addEmail(a, false), onSuccess: (d) => { refresh(d); setAddress(""); } });
  const remove = useMutation({ mutationFn: api.removeEmail, onSuccess: (d) => { refresh(d); setRemoving(null); } });
  if (!q.data) return null;
  const { addresses, pending, available } = q.data;
  if (!available && addresses.length === 0) return null;
  return (
    <section className="card stack-sm addresses">
      <div><div className="k" style={{ fontWeight: 500 }}>{txt.yourEmailAddresses}</div>
        <div className="muted small">{txt.onlyConfirmedOnesCan}</div></div>
      <div className="prows">
        {addresses.map((e: UserEmail) => (
          <div key={e.id} className="addr-row">
            <div className="addr-row-main">
              <span className={`addr${e.confirmed ? "" : " muted"}`}>{e.address}</span>
              <span className="chips">
                {e.undeliverable ? <span className="chip-s warn">{txt.weCouldnTDeliver2}</span> : <span className={`chip-s${e.confirmed ? " ok" : ""}`}>{e.confirmed ? txt.confirmed : txt.notConfirmed2}</span>}
                {e.forNotifications && <span className="chip-s">{txt.getsNotifications}</span>}
                {e.isRelay && <span className="chip-s">{txt.hiddenByApple}</span>}
                <span className="chip-s">{SOURCE[e.source]}</span>
              </span>
              {!e.confirmed && available && !pending && <button type="button" className="linkish small" disabled={add.isPending} onClick={() => add.mutate(e.address)}>{txt.confirmIt}</button>}
            </div>
            <button type="button" className="linkish small muted" onClick={() => (e.confirmed ? setRemoving(e.id) : remove.mutate(e.id))}>{txt.remove}</button>
            {removing === e.id && (
              <div className="confirm">
                <p className="small">{txt.removeYouWonT(e.address)}</p>
                <div className="row"><button type="button" className="btn danger sm" disabled={remove.isPending} onClick={() => remove.mutate(e.id)}>{txt.remove2}</button>
                  <button type="button" className="btn ghost sm" onClick={() => setRemoving(null)}>{txt.keep}</button></div>
              </div>
            )}
          </div>
        ))}
      </div>
      {pending && <PendingCode pending={pending} onConfirmed={() => setAdding(false)} resending={add.isPending} onResend={() => add.mutate(pending.address)} />}
      {available && !pending && (adding ? (
        <form className="addr-new" onSubmit={(e) => { e.preventDefault(); if (address.trim()) add.mutate(address); }}>
          <label htmlFor="add-email" className="small" style={{ fontWeight: 600 }}>{txt.newAddress}</label>
          <input id="add-email" className="input" type="email" autoComplete="email" placeholder="you@example.com" value={address} onChange={(e) => setAddress(e.target.value)} autoFocus />
          <div className="row"><button type="submit" className="btn primary sm" disabled={!address.trim() || add.isPending}>{txt.sendItACode}</button>
            <button type="button" className="btn ghost sm" onClick={() => setAdding(false)}>{txt.cancel}</button></div>
        </form>
      ) : <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} onClick={() => setAdding(true)}>{txt.addAnAddress}</button>)}
      {(add.isError || remove.isError) && <p className="error small">{describeError(add.error ?? remove.error)}</p>}
    </section>
  );
}
